import React, { useMemo, useState } from "react";
import { Box, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useKivunovaTranslation } from "@kivunova/kivufrontendcommon";
import CommonLoader from "../../../common/CommonLoader/CommonLoader";
import AppButton from "../../../common/AppButton";
import {
  useAcademicPrograms,
  useAllGrades,
  useCreateGrade
} from "../../../../hooks/api/education/useAcademicStructure";
import { formatEntityStatus, isArchived } from "../common/academicUtils";

const ALL_PROGRAMS = "";

const GradeList: React.FC = () => {
  const { t } = useKivunovaTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const programFilter = searchParams.get("programId") ?? ALL_PROGRAMS;
  const { data: programs = [] } = useAcademicPrograms();
  const {
    data: grades = [],
    isPending,
    refetch
  } = useAllGrades(programFilter || undefined);
  const createGrade = useCreateGrade();
  const [gradeName, setGradeName] = useState("");
  const [createProgramId, setCreateProgramId] = useState(
    programFilter || programs[0]?.id || ""
  );

  const programNameById = useMemo(
    () => new Map(programs.map((program) => [program.id, program.name])),
    [programs]
  );

  const sortedGrades = useMemo(
    () =>
      [...grades].sort((a, b) => {
        const programCompare = (
          programNameById.get(a.programId) ?? ""
        ).localeCompare(programNameById.get(b.programId) ?? "");
        if (programCompare !== 0) {
          return programCompare;
        }
        return a.name.localeCompare(b.name);
      }),
    [grades, programNameById]
  );

  if (isPending) {
    return (
      <CommonLoader loadingMessage={t("loading-grades", "Loading grades...")} />
    );
  }

  return (
    <Box data-testid="grade-list-page" sx={{ p: 3 }}>
      <Typography variant="h5" component="h1" sx={{ mb: 2 }}>
        {t("grades-title", "Grades")}
      </Typography>

      <TextField
        select
        size="small"
        fullWidth
        sx={{ mb: 2, maxWidth: 360 }}
        label={t("program-filter-label", "Program")}
        value={programFilter}
        onChange={(event) => {
          const value = event.target.value;
          if (value) {
            setSearchParams({ programId: value });
          } else {
            setSearchParams({});
          }
          setCreateProgramId(value || programs[0]?.id || "");
        }}
      >
        <MenuItem value={ALL_PROGRAMS}>
          {t("all-programs", "All programs")}
        </MenuItem>
        {programs.map((program) => (
          <MenuItem key={program.id} value={program.id}>
            {program.name}
          </MenuItem>
        ))}
      </TextField>

      <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
        <TextField
          select
          size="small"
          sx={{ minWidth: 200 }}
          label={t("program-select-label", "Program")}
          value={createProgramId}
          onChange={(event) => setCreateProgramId(event.target.value)}
        >
          {programs.map((program) => (
            <MenuItem key={program.id} value={program.id}>
              {program.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          fullWidth
          label={t("grade-name-label", "Grade name")}
          value={gradeName}
          onChange={(event) => setGradeName(event.target.value)}
          disabled={!createProgramId}
        />
        <AppButton
          disabled={
            !createProgramId || !gradeName.trim() || createGrade.isPending
          }
          onClick={async () => {
            await createGrade.mutateAsync({
              programId: createProgramId,
              name: gradeName.trim()
            });
            setGradeName("");
            refetch();
          }}
        >
          {t("add-grade", "Add grade")}
        </AppButton>
      </Stack>

      {sortedGrades.length === 0 ? (
        <Typography color="text.secondary">
          {t("grades-list-empty", "No grades found.")}
        </Typography>
      ) : (
        <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none" }}>
          {sortedGrades.map((grade) => (
            <Box
              component="li"
              key={grade.id}
              sx={{
                py: 1.5,
                borderBottom: "1px solid",
                borderColor: "divider",
                display: "flex",
                justifyContent: "space-between",
                opacity: isArchived(grade.status) ? 0.65 : 1
              }}
            >
              <Box>
                <Typography variant="body1" fontWeight={600}>
                  {grade.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {programNameById.get(grade.programId) ?? grade.programId}
                  {" · "}
                  {formatEntityStatus(grade.status)}
                </Typography>
              </Box>
              <AppButton variant="text" onClick={() => navigate(grade.id)}>
                {t("view", "View")}
              </AppButton>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default GradeList;
